import {Component, Inject, OnInit} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from "@angular/material/dialog";
import {MatTab, MatTabGroup} from "@angular/material/tabs";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {FormsModule} from "@angular/forms";
import {NgForOf, NgIf, NgTemplateOutlet} from "@angular/common";
import {Broadcast} from "../../model/broadcast/broadcast";
import {Player} from "../../model/broadcast/player";
import {BroadcastsService} from "../../services/broadcasts.service";

@Component({
  selector: 'app-roster-dialog',
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatTab,
    MatTabGroup,
    MatFormFieldModule,
    MatInputModule,
    MatButton,
    MatIconButton,
    MatIcon,
    FormsModule,
    NgForOf,
    NgIf,
    NgTemplateOutlet
  ],
  templateUrl: './roster-dialog.component.html',
  styleUrl: './roster-dialog.component.scss'
})
export class RosterDialogComponent implements OnInit {

  awayRoster: Player[] = [];
  homeRoster: Player[] = [];
  otherPlayers: Player[] = [];
  reimporting = false;

  constructor(
    public dialogRef: MatDialogRef<RosterDialogComponent>,
    public broadcastsService: BroadcastsService,
    @Inject(MAT_DIALOG_DATA) public broadcast: Broadcast,
  ) { }

  get awayTeamId(): string {
    return this.broadcast.config.awayConfig.id;
  }

  get homeTeamId(): string {
    return this.broadcast.config.homeConfig.id;
  }

  ngOnInit(): void {
    const roster = this.broadcast.fullRosters ?? [];
    const sortByNumber = (a: Player, b: Player) => (Number(a.number) || 0) - (Number(b.number) || 0);
    this.awayRoster = roster
      .filter(p => p.teamGuid == this.awayTeamId)
      .map(p => ({...p}))
      .sort(sortByNumber);
    this.homeRoster = roster
      .filter(p => p.teamGuid == this.homeTeamId)
      .map(p => ({...p}))
      .sort(sortByNumber);
    this.otherPlayers = roster
      .filter(p => p.teamGuid != this.awayTeamId && p.teamGuid != this.homeTeamId)
      .map(p => ({...p}));
  }

  // Pull both squads from leagues/{leagueId}/rosters again and drop them into
  // the open dialog, without saving — the operator still presses Update.
  //
  // This is the way back for a broadcast created before the server had scraped
  // the rosters (or one that lost them to the write race fixed in
  // BroadcastsService.createBroadcast): until now the dialog could only edit
  // what the broadcast already held, so an empty one meant typing both squads
  // in by hand.
  //
  // Hand-added players (no guid) survive the reimport; imported ones are
  // replaced, which is what "load them again" has to mean.
  reimport(): void {
    if (!this.broadcast.leagueId || this.reimporting) return;
    this.reimporting = true;
    this.broadcastsService.loadRosters(this.broadcast.leagueId, this.homeTeamId, this.awayTeamId)
      .then(players => {
        this.homeRoster = this.merge(this.homeRoster, players, this.homeTeamId);
        this.awayRoster = this.merge(this.awayRoster, players, this.awayTeamId);
      })
      .finally(() => this.reimporting = false);
  }

  private merge(current: Player[], fetched: Player[], teamId: string): Player[] {
    const sortByNumber = (a: Player, b: Player) => (Number(a.number) || 0) - (Number(b.number) || 0);
    const added = current.filter(p => !p.guid);
    const imported = fetched.filter(p => p.teamGuid == teamId).map(p => ({...p}));
    return [...imported.sort(sortByNumber), ...added];
  }

  addPlayer(roster: Player[], teamId: string): void {
    // No guid yet — only freshly added (unsaved) players are removable.
    // A guid is assigned on save in update().
    roster.push({
      guid: null,
      teamGuid: teamId,
      name: '',
      number: null,
      batting: true,
    });
  }

  addOther(): void {
    // "Other" people are not tied to a team (no team colors) and carry a
    // custom title in the `info` field instead of a jersey number.
    this.otherPlayers.push({
      guid: null,
      teamGuid: null,
      name: '',
      info: '',
      batting: false,
    });
  }

  removePlayer(roster: Player[], index: number): void {
    roster.splice(index, 1);
  }

  update(): void {
    const cleanedAway = this.awayRoster.filter(p => p.name?.trim());
    const cleanedHome = this.homeRoster.filter(p => p.name?.trim());
    const cleanedOther = this.otherPlayers.filter(p => p.name?.trim());
    // Newly added entries keep guid = null on purpose: that's how the dialog
    // tells them apart from imported roster players, so they stay removable.
    this.broadcast.fullRosters = [...cleanedOther, ...cleanedAway, ...cleanedHome];
    this.syncRosterToBroadcast();
    this.broadcastsService.updateRosterWithSync(this.broadcast).then(() => {
      this.dialogRef.close('update');
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  private syncRosterToBroadcast(): void {
    const byGuid = new Map<string, Player>();
    this.broadcast.fullRosters.forEach(p => {
      if (p.guid) byGuid.set(p.guid, p);
    });

    const apply = (player: Player | undefined | null): void => {
      if (!player || !player.guid) return;
      const source = byGuid.get(player.guid);
      if (source) {
        player.name = source.name;
        player.number = source.number;
        if (source.info !== undefined) {
          player.info = source.info;
        }
      }
    };

    const lineups = this.broadcast.manualLineups;
    if (lineups) {
      lineups.homeLineup?.forEach(apply);
      lineups.awayLineup?.forEach(apply);
      apply(lineups.homePitcher);
      apply(lineups.awayPitcher);
    }
    apply(this.broadcast.lowerThird);
  }
}
